interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class DataCache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  // Clear cache for a specific user when they sign out
  clearUserCache(userId: string): void {
    const keysToDelete: string[] = [];
    for (const [key] of this.cache) {
      if (key.includes(userId)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  // Get cache stats for debugging
  getStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }
}

export const dataCache = new DataCache();

// Helper function to create cache keys
export const createCacheKey = (prefix: string, ...params: (string | number)[]): string => {
  return `${prefix}:${params.join(':')}`;
};

// Cache TTL constants
export const CACHE_TTL = {
  DASHBOARD: 2 * 60 * 1000, // 2 minutes for dashboard (needs frequent updates)
  ORDERS: 10 * 60 * 1000, // 10 minutes for orders
  SHIPMENTS: 15 * 60 * 1000, // 15 minutes for shipments
  REPLENISHABLES: 30 * 60 * 1000, // 30 minutes for replenishables
  INVENTORY: 20 * 60 * 1000, // 20 minutes for inventory
  REPORTS: 60 * 60 * 1000, // 1 hour for reports
  SETTINGS: 24 * 60 * 60 * 1000, // 24 hours for settings
} as const;
